import {
  MonitoredByCompanyTypes,
  MonitoringBiddingTypes,
} from 'store/modules/monitoring_store/types';
import { ProfileData } from 'store/modules/profile_store/types';
import { firstReleasedCompany, getSelectedCompanies } from './selected_companies';
import tabId from './tabId';

export const monitoringCompanies = (
  profile: ProfileData,
  activeCompany: number | undefined,
): number[] | undefined => {
  if (!profile.representant) return undefined;

  const selected = getSelectedCompanies(profile);
  if (!activeCompany || selected.includes(activeCompany)) return selected;

  return [activeCompany];
};

export const monitoringListKey = (
  companies: number[] | undefined,
  activeCompany: number | undefined,
): string => (companies && companies.length > 1 ? companies.join(',') : String(activeCompany));

export const tradingOwnerCompany = (
  profile: ProfileData,
  trading:
    | Pick<MonitoringBiddingTypes, 'monitored_by_companies' | 'monitored_company_id'>
    | undefined,
  tab: string = tabId(),
): number | null => {
  const companies = trading?.monitored_by_companies || [];
  if (!profile.representant || companies.length === 0) return null;

  const owner = trading?.monitored_company_id ?? companies[0].id;

  return owner === profile.currentCompany?.[tab] ? null : owner;
};

export const companyToRestore = (profile: ProfileData, tab: string = tabId()): number | null => {
  if (!profile.representant) return null;

  const selected = getSelectedCompanies(profile, tab);
  if (selected.length < 2) return null;

  const active = firstReleasedCompany(profile, selected) ?? null;

  return profile.currentCompany?.[tab] === active ? null : active;
};

export const monitoringSubscriptionCompany = (
  profile: ProfileData,
  tab: string = tabId(),
): number | undefined => companyToRestore(profile, tab) ?? profile.currentCompany?.[tab];

export type CompanyAction = 'archive' | 'important' | 'unmonitor';

export const ownCompanies = (
  companies: MonitoredByCompanyTypes[] | undefined,
): MonitoredByCompanyTypes[] => (companies || []).filter((company) => company.proprio);

export const asksCompany = (companies: MonitoredByCompanyTypes[] | undefined): boolean =>
  ownCompanies(companies).length > 1;

export const initiallyChecked = (
  action: CompanyAction,
  companies: MonitoredByCompanyTypes[] | undefined,
): number[] =>
  ownCompanies(companies)
    .filter((company) =>
      action === 'archive' ? company.arquivado : action === 'important' && company.importante,
    )
    .map((company) => company.id);

export const companyUnavailable = (
  action: CompanyAction,
  company: MonitoredByCompanyTypes,
): string | null => {
  if (!company.proprio) return 'Monitorado só por colega';
  if (action === 'unmonitor' && company.bloqueio) return company.bloqueio;

  return null;
};

export const availableCompanies = (
  action: CompanyAction,
  companies: MonitoredByCompanyTypes[] | undefined,
): MonitoredByCompanyTypes[] =>
  (companies || []).filter((company) => !companyUnavailable(action, company));

export const toggleAllCompanies = (
  action: CompanyAction,
  companies: MonitoredByCompanyTypes[] | undefined,
  checked: number[],
): number[] => {
  const ids = availableCompanies(action, companies).map((company) => company.id);
  const allChecked = ids.length > 0 && ids.every((id) => checked.includes(id));

  return allChecked
    ? checked.filter((id) => !ids.includes(id))
    : Array.from(new Set([...checked, ...ids]));
};

export const companiesToChange = (
  action: CompanyAction,
  companies: MonitoredByCompanyTypes[] | undefined,
  checked: number[],
): number[] =>
  ownCompanies(companies)
    .filter((company) => !companyUnavailable(action, company))
    .filter((company) => {
      const wanted = checked.includes(company.id);
      if (action === 'archive') return wanted !== Boolean(company.arquivado);
      if (action === 'important') return wanted !== Boolean(company.importante);
      return wanted;
    })
    .map((company) => company.id);
